import { useEffect, useMemo, useState } from 'react';
import useMenus from '../context/MenuContext';
import useParam from '../context/ParamContext';
import dayjs from 'dayjs';
import useUsers from '../context/UsersContext';
import { generateAutocompleteItems } from '../utils/helper';
import masterDataService from '../service/masterData.service';
import jokerService from '../service/joker.service';
import { JokerTypes } from '../config/joker';
import { getResponsibleList } from '../utils/getResponsibleList';
import { ItemGroupType } from '../config/itemGroup';

export const useProject = () => {
  const { menus, getMenus, getMenuSubMenuId, getSubMenuNamesByUrls, getCanEdit, getCanCopy, getCanDelete } = useMenus();
  const { getParam, params } = useParam();
  const { user, getUser } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [submenu, setSubmenu] = useState(null);
  const [simpleTaskSubmenu, setSimpleTaskSubmenu] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canCopy, setCanCopy] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [dynProps, setDynProps] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    status: [],
    company: [],
    province: [],
    resourceOne: [],
    resourceTwo: [],
    resourceThree: [],
    resourceFour: [],
    resourceFive: [],
    taskList: [],
    user: [],
    taskListStatus: [],
    taskListType: [],
    classification: [],
    costCenter: [],
    companyLocation: [],
    projectTypeStatus: [],
    currency: [],
    item: [],
    serviceTypeItem: [],
    employee: [],
    qtyUnit: [],
  });

  const [loading, setLoading] = useState(true);
  const [dynLoading, setDynLoading] = useState(true);
  const [jokerLoading, setJokerLoading] = useState(true);
  const isLoading = loading || dynLoading || jokerLoading;
  const [companyContacts, setCompanyContacts] = useState([]);
  const [autocompleteData, setAutocompleteData] = useState({});
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerLineFields, setJokerLineFields] = useState([]);

  const getJokerFields = () => {
    jokerService
      .getFields(JokerTypes.PROJECT)
      .then((data) => {
        const [headFields, lineFields] = data.reduce(
          (acc, field) => {
            if (field.isRow) {
              acc[1].push(field);
            } else {
              acc[0].push(field);
            }
            return acc;
          },
          [[], []]
        );

        setJokerFields(headFields);
        setJokerLineFields(lineFields);
      })
      .finally(() => {
        setJokerLoading(false);
      });
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getMenus();
    getParam();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('Project');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
    setCanCopy(getCanCopy(submenuId));
    setCanDelete(getCanDelete(submenuId));
    const simpleTaskSubmenuId = getMenuSubMenuId('simpleTask');
    setSimpleTaskSubmenu(simpleTaskSubmenuId);
  }, [menus]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['resourceOne', 'resourceTwo', 'resourceThree', 'resourceFour', 'machine']);
  }, [menus]);

  const def = {
    projectId: null,
    projectName: '',
    companyShortName: '',
    companyId: null,
    statusId: null,
    projectStartDate: dayjs(new Date()),
    projectEndDate: dayjs(new Date()),
    projectPrepare: '',
    userId: user, //@TODO legyeb disabled szerintem? Kérdezzük meg Attilát
    projectDescription: '',
    projectGroupId: null,
    orderNumber: '',
    active: true,
    provinceId: null,
    generatedName: '',
    communication: [],
    contact: [],
    resourceFive: [],
    resourceFour: [],
    resourceThree: [],
    resourceTwo: [],
    resourceOne: [],
    taskList: [],
    companyContactId: null,
    projectTypeId: null,
    classificationId: null,
    projectTypeStatusId: null,
    costCenterCode: '',
    companyLocationId: null,
    joker_char1: '',
    joker_char2: '',
    joker_char3: '',
    joker_char4: '',
    joker_char5: '',
    joker_char6: '',
    joker_char7: '',
    joker_char8: '',
    joker_char9: '',
    joker_char10: '',
    joker_date1: null,
    joker_date2: null,
    joker_date3: null,
    joker_date4: null,
    joker_date5: null,
    joker_num1: '',
    joker_num2: '',
    joker_num3: '',
    joker_num4: '',
    joker_num5: '',
    joker_num6: '',
    joker_num7: '',
    joker_num8: '',
    joker_num9: '',
    joker_num10: '',
    joker_num11: '',
    joker_num12: '',
    joker_num13: '',
    joker_num14: '',
    joker_num15: '',
    joker_int1: '',
    joker_int2: '',
    joker_int3: '',
    joker_int4: '',
    joker_int5: '',
    projectPrice: [],
    projectEmployee: [],
    projectPaymentSchedule: [],
    responsiblePersonId: null,
    projectJokerLine: [],
  };

  useEffect(() => {
    if (submenu) {
      initDefaultValues();
    }
  }, [submenu]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .provinceToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceTwoToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceThreeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceFourToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceFiveToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .taskListTemplatesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    if (simpleTaskSubmenu) {
      promiseArray.push(
        new Promise((resolve, reject) => {
          masterDataService
            .statusToForm(simpleTaskSubmenu)
            .then((data) => {
              resolve(data);
            })
            .catch((e) => {
              reject(e);
            });
        })
      );

      promiseArray.push(
        new Promise((resolve, reject) => {
          masterDataService
            .typeToForm(simpleTaskSubmenu)
            .then((data) => {
              resolve(data);
            })
            .catch((e) => {
              reject(e);
            });
        })
      );
    }

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .classificationToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costCenterToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyLocationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(getResponsibleList());

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeStatusToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .currencyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .itemsFromTableToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          status: resp[0],
          company: resp[1],
          province: resp[2],
          resourceOne: resp[3],
          resourceTwo: resp[4],
          resourceThree: resp[5],
          resourceFour: resp[6],
          resourceFive: resp[7],
          taskList: resp[8],
          user: resp[9],
          userGroups: resp[10],
          taskListStatus: resp[11],
          taskListType: resp[12],
          projectType: resp[13],
          classification: resp[14],
          costCenter: resp[15],
          companyLocation: resp[16],
          responsible: resp[17],
          projectTypeStatus: resp[18],
          currency: resp[19],
          item: resp[20],
          employee: resp[21],
          qtyUnit: resp[22],
        };
        setAutocompleteData(aData);

        const filteredCostCenter = aData.costCenter.filter((cc) => !cc.deletedBy);
        const filteredItem = aData.item.filter((item) => item.type === ItemGroupType.SERVICE);

        const acOptions = {
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          province: generateAutocompleteItems(aData.province, 'name', 'id'),
          resourceOne: generateAutocompleteItems(aData.resourceOne, 'name', 'id'),
          resourceTwo: generateAutocompleteItems(aData.resourceTwo, 'name', 'id'),
          resourceThree: generateAutocompleteItems(aData.resourceThree, 'name', 'id'),
          resourceFour: generateAutocompleteItems(aData.resourceFour, 'name', 'id'),
          resourceFive: generateAutocompleteItems(aData.resourceFive, 'machineName', 'machineId'),
          taskList: generateAutocompleteItems(aData.taskList, 'name', 'id'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          userGroups: generateAutocompleteItems(aData.userGroups, 'name', 'id'),
          taskListStatus: generateAutocompleteItems(aData.taskListStatus.statusItems, 'itemName', 'itemId'),
          taskListType: generateAutocompleteItems(aData.taskListType.typeItems, 'itemName', 'itemId'),
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
          classification: generateAutocompleteItems(aData.classification?.classificationItem, 'name', 'id'),
          costCenter: generateAutocompleteItems(filteredCostCenter, ['code', 'name'], 'code'),
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
          responsible: aData.responsible,
          projectTypeStatus: generateAutocompleteItems(aData.projectTypeStatus, ['typeName', 'name'], 'id'),
          currency: generateAutocompleteItems(aData.currency, 'code', 'code'),
          item: generateAutocompleteItems(aData.item, ['itemCode', 'itemName'], 'itemCode'),
          serviceTypeItem: generateAutocompleteItems(filteredItem, 'itemName', 'itemCode'),
          employee: generateAutocompleteItems(aData.employee, 'name', 'id'),
          qtyUnit: generateAutocompleteItems(aData.qtyUnit, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
        setLoading(false);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  return {
    refetchTable: refetchTable || false,
    setRefetchTable: setRefetchTable || (() => {}),
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    submenu,
    submenuNames,
    params,
    canEdit,
    rowSelectionModel,
    setRowSelectionModel,
    autoCompleteOptions,
    setAutoCompleteOptions,
    dynProps,
    setDynProps,
    loading,
    setLoading,
    dynLoading,
    setDynLoading,
    isLoading,
    simpleTaskSubmenu,
    companyContactsState: { companyContacts, setCompanyContacts },
    def,
    autocompleteData,
    setAutocompleteData,
    canCopy,
    canDelete,
    jokerFields,
    setJokerFields,
    getJokerFields,
    jokerLoading,
    jokerLineFields,
  };
};
